"use client";

import { useEffect } from "react";
import {
  isChunkLoadFailure,
  shouldSuppressClientErrorReport,
} from "@/lib/browser-console-noise";
import {
  formatConsoleError,
  reportClientError,
} from "@/lib/client-error-reporting";
import { logger } from "@/lib/logger";
import { stringify } from "@/lib/shared-utils";

const BENIGN_PATTERNS = [
  /ResizeObserver loop limit exceeded/i,
  /ResizeObserver loop completed with undelivered notifications/i,
];

const HYDRATION_PATTERNS = [
  /hydration/i,
  /did not match/i,
  /Minified React error #418/i,
  /Minified React error #423/i,
  /Minified React error #425/i,
];

function isHydrationError(message: string): boolean {
  return HYDRATION_PATTERNS.some((pattern) => pattern.test(message));
}

function isBenignViewTransitionRejection(reason: unknown): boolean {
  // 2026: View Transition lifecycle rejections are expected when document becomes hidden mid-transition
  // (InvalidStateError "Transition was aborted because of invalid state" on Chrome 146+).
  // They surface as unhandledrejection without a handler on ViewTransition.ready/finished.
  const isDomException =
    typeof DOMException !== "undefined" && reason instanceof DOMException;
  if (isDomException) {
    const dom = reason as DOMException;
    if (
      ["AbortError", "InvalidStateError", "TimeoutError"].includes(dom.name)
    ) {
      const m = (dom.message ?? "").toLowerCase();
      if (
        m.includes("transition") ||
        m.includes("aborted") ||
        m.includes("skipped") ||
        m.includes("invalid state") ||
        m.includes("visibility state is hidden")
      )
        return true;
      // Chrome's generic "Transition was aborted because of invalid state" always has transition wording,
      // but be permissive for name-only cases where transition object discarded (ready rejected hidden)
      if (dom.name === "InvalidStateError" || dom.name === "AbortError") {
        // Only suppress if message hints transition; otherwise keep for real InvalidStateError (e.g. MediaRecorder)
        return m.length === 0 ? false : m.includes("transition");
      }
    }
  }
  return false;
}

function shouldIgnoreClientReport(
  message: unknown,
  userAgent?: string,
): boolean {
  if (isBenignViewTransitionRejection(message)) return true;
  const msg = stringify(message) || String(message ?? "");
  if (BENIGN_PATTERNS.some((pattern) => pattern.test(msg))) return true;
  // Lote Fase 5 (reportar hidratación real): un mismatch SSR/cliente (#418,
  // #423, #425, "did not match") es un bug real de la app y SE REPORTA a
  // Sentry + /api/error-report. Solo se sigue ignorando cuando el texto trae
  // además un marcador de extensión conocida (el content-script rompió el DOM,
  // no nuestro SSR) — eso ya lo cubre shouldSuppressClientErrorReport abajo.
  if (isHydrationError(msg)) {
    if (shouldSuppressClientErrorReport(msg, userAgent)) return true;
    return false;
  }
  if (shouldSuppressClientErrorReport(msg, userAgent)) return true;
  // ChunkLoadError is recovered by ErrorBoundary reload; avoid double Discord spam
  // from window.error + console.error while reload is in flight.
  if (isChunkLoadFailure(msg)) return true;
  return false;
}

export function ClientErrorReporter() {
  useEffect(() => {
    // 2026-09-10 Sentry dev-gate: en dev o sin DSN no se instalan listeners
    // (0 eventos Sentry, solo logger.warn). Prod intacto. Tests (NODE_ENV=test)
    // ejercen la ruta prod para no romper paridad.
    if (
      process.env.NODE_ENV !== "test" &&
      process.env.NODE_ENV !== "production"
    ) {
      logger.warn("[client-error-reporter] disabled (dev, no Sentry)");
      return;
    }
    try {
      if (
        !process.env.NEXT_PUBLIC_SENTRY_DSN &&
        process.env.NODE_ENV === "production"
      ) {
        logger.warn("[client-error-reporter] disabled (no DSN, no Sentry)");
        return;
      }
    } catch {
      return;
    }
    const origRef = console.error.bind(console);
    const ua = navigator.userAgent;

    console.error = (...args: unknown[]) => {
      origRef(...args);
      const msg = formatConsoleError(args);
      if (shouldIgnoreClientReport(msg, ua)) return;
      void reportClientError({
        message: msg,
        locationHref: window.location.href,
        pathname: window.location.pathname,
        userAgent: ua,
      });
    };

    const handleError = (event: ErrorEvent) => {
      // 2026-09-07 Sentry JAVASCRIPT-NEXTJS-E: `event.error` a veces llega como
      // objeto vacío `{}` (no-Error) y producía issues inútiles `Error: {}`.
      // Se prefiere el Error real; si no lo hay, el mensaje de texto.
      const realError = event.error instanceof Error ? event.error : undefined;
      const msg = String(event.message ?? "");
      const stack = realError?.stack;
      // 2026-09-03 extension attribution: content scripts running in page
      // context (pdfeditor inject.js) surface with the page URL as file, so the
      // noise filter must also see stack+filename. Report payload unchanged.
      const combined = [msg, stack, event.filename].filter(Boolean).join("\n");
      if (shouldIgnoreClientReport(combined, ua)) return;
      void reportClientError({
        message: realError ?? event.message,
        stack,
        locationHref: window.location.href,
        pathname: window.location.pathname,
        userAgent: ua,
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      if (isBenignViewTransitionRejection(reason)) {
        try {
          event.preventDefault();
        } catch {}
        return;
      }
      // 2026-09-03 same extension attribution as handleError: the filter must
      // see the rejection stack (inject.js frames), not just the message.
      const reasonText = [
        stringify(reason) || String(reason ?? ""),
        reason instanceof Error ? reason.stack : undefined,
      ]
        .filter(Boolean)
        .join("\n");
      if (shouldIgnoreClientReport(reasonText, ua)) {
        // Silence benign / extension unhandledrejections in console so third-party
        // scripts (e.g. Urban VPN 200.js M_ID, view-transition aborts) don't spam DevTools.
        try {
          event.preventDefault();
        } catch {}
        return;
      }
      void reportClientError({
        message: reason,
        stack: reason instanceof Error ? reason.stack : undefined,
        locationHref: window.location.href,
        pathname: window.location.pathname,
        userAgent: ua,
      });
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      console.error = origRef;
      window.removeEventListener("error", handleError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
    };
  }, []);

  return null;
}
